import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiPainel from "../services/apiPainel";
import { conectarSocketPainel } from "../services/socketPainel";
import { FaArrowRight, FaUndo, FaCheck, FaTimes, FaCat, FaHistory, FaTv, FaListOl, FaTrophy, FaBan } from "react-icons/fa";
import "../styles/painelRodizio.css";

export default function PainelRodizio() {
  const navigate = useNavigate();

  const [operador, setOperador] = useState(null);
  const [rodizio, setRodizio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [encaixeNumero, setEncaixeNumero] = useState("");

  const eventoId = operador?.eventoId;
  const categoria = operador?.categoria;

  // Permissões
  const podeJulgar = useMemo(() => operador?.funcao?.toLowerCase().includes("juiz"), [operador]);
  const podeLocutor = useMemo(() => operador?.funcao?.toLowerCase().includes("locutor"), [operador]);

  useEffect(() => {
    const op = JSON.parse(localStorage.getItem("painelOperador") || "null");
    if (!op) {
      navigate("/painel");
      return;
    }
    setOperador(op);
  }, [navigate]);

  async function carregarRodizio() {
    if (!eventoId || !categoria) return;
    try {
      const { data } = await apiPainel.get(`/rodizio/estado/${eventoId}/${categoria}`);
      setRodizio(data?.rodizio || null);
    } catch (err) { console.error(err); }
  }

  useEffect(() => {
    if (!eventoId || !categoria) return;
    carregarRodizio();
    const socket = conectarSocketPainel();
    const channel = `rodizio:${eventoId}:${categoria}:update`;
    
    socket.on(channel, () => {
      console.log("⚡ Update Socket");
      carregarRodizio();
    });

    return () => {
      socket.off(channel);
      socket.disconnect();
    };
  }, [eventoId, categoria]);

  // Ações API
  const executarAcao = async (url, body) => {
    if(!operador) return;
    setLoading(true);
    try {
        const payload = { ...body, operador: { nome: operador.nome, funcao: operador.funcao } };
        const { data } = await apiPainel.post(url, payload);
        setRodizio(data?.rodizio || null);
        if(body.numeroSenha) setEncaixeNumero(""); 
    } catch(e) {
        alert(e?.response?.data?.error || e.message);
    } finally {
        setLoading(false);
    }
  };

  const iniciarRodizio = () => executarAcao("/rodizio/iniciar", {
    eventoId, categoria, 
    senhaInicial: operador.senhaInicial, 
    senhaFinal: operador.senhaFinal,
    tamanhoRodizio: operador.tamanhoRodizio,
    boisParaClassificar: operador.boisParaClassificar
  });

  const enviarAcao = (acao) => executarAcao("/rodizio/acao", { eventoId, categoria, acao });
  const encaixar = () => executarAcao("/rodizio/encaixar", { eventoId, categoria, numeroSenha: Number(encaixeNumero) });
  const classificarRodada = (senha, rodada) => executarAcao("/rodizio/classificar", { eventoId, categoria, numeroSenha: senha, rodada });

  // 🔹 DADOS COMPUTADOS
  const atual = rodizio?.fila?.[0] || null;
  const proximos = (rodizio?.fila || []).slice(1, 4);
  const totalFila = rodizio?.fila?.length || 0;
  const restamCorrer = totalFila > 0 ? totalFila - 1 : 0;

  return (
    <div className="pr-container">
      {/* HEADER */}
      <header className="pr-header">
        <div className="pr-header-title">
          <h2><FaTv /> Painel de Corrida</h2>
          <div className="pr-header-info">
            Olá, <b>{operador?.nome}</b> ({operador?.funcao}) • Categoria: <span style={{color:"#fdd212"}}>{categoria}</span>
          </div>
        </div>
        <button onClick={() => navigate("/painel")} className="pr-btn-sair">Sair</button>
      </header>

      <div className="pr-grid">
        
        {/* COLUNA ESQUERDA: CARD ATUAL + PRÓXIMOS */}
        <div className="pr-main-column">
          <div className="pr-main-card">
            {!rodizio ? (
              <div style={{textAlign:"center", padding: 40}}>
                <p>Rodízio não iniciado.</p>
                <button className="pr-btn-action btn-success" onClick={iniciarRodizio} disabled={loading} style={{margin:"0 auto", width:"200px"}}>
                   INICIAR AGORA
                </button>
              </div>
            ) : !atual ? (
                <div style={{textAlign:"center", padding: 40, opacity:0.7}}>
                    <h3>Aguardando senhas...</h3>
                    <p>Fila vazia para este bloco.</p>
                </div>
            ) : (
                <>
                    {/* Linha Superior: Senha e Boi */}
                    <div className="pr-status-row">
                        <div className="pr-senha-big">#{atual.numeroSenha}</div>
                        <div className="pr-boi-counter">
                            <span>BOI ATUAL</span>
                            <strong>{atual.boiAtual}º</strong>
                        </div>
                    </div>

                    {/* Detalhes - ORDEM SOLICITADA */}
                    <div className="pr-details-grid">
                        
                        {/* 1. VAQUEIRO (Largura total) */}
                        <div className="pr-detail-item full-width">
                            <span className="pr-detail-label">Vaqueiro / Puxador</span>
                            <span className="pr-detail-value">{atual.nomeVaqueiro || "---"}</span>
                        </div>

                        {/* 2. ESTEIRA (Largura total) */}
                        <div className="pr-detail-item full-width">
                            <span className="pr-detail-label">Esteira</span>
                            <span className="pr-detail-value">{atual.nomeEsteira || "---"}</span>
                        </div>

                        {/* 3. CAVALO PUXAR */}
                        <div className="pr-detail-item">
                            <span className="pr-detail-label">Cavalo de Puxar</span>
                            <span className="pr-detail-value">{atual.cavaloPuxar || "---"}</span>
                        </div>

                        {/* 4. CAVALO ESTEIRA */}
                        <div className="pr-detail-item">
                            <span className="pr-detail-label">Cavalo de Esteira</span>
                            <span className="pr-detail-value">{atual.cavaloEsteira || "---"}</span>
                        </div>

                        {/* 5. REPRESENTAÇÃO + CIDADE (Largura total) */}
                        <div className="pr-detail-item full-width">
                            <span className="pr-detail-label">Representação - Cidade</span>
                            <span className="pr-detail-value">
                                {atual.representacao || "---"} - {atual.cidade || ""}
                            </span>
                        </div>

                        {/* 6. BOI TV (Largura total) */}
                        <div className="pr-detail-item full-width">
                            <span className="pr-detail-label">BOI TV</span>
                            <span className="pr-detail-value" style={{color: atual.boiTv ? "#2ecc71" : "#fff", fontWeight:"800"}}>
                                {atual.boiTv ? "SIM (COMPRADO)" : "NÃO"}
                            </span>
                        </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="pr-actions-grid">
                        {(podeLocutor || podeJulgar) && (
                            <>
                                <button className="pr-btn-action btn-secondary" onClick={() => enviarAcao("VOLTAR")} disabled={loading}>
                                    <FaUndo /> VOLTAR
                                </button>
                                <button className="pr-btn-action btn-secondary" onClick={() => enviarAcao("RETORNO")} disabled={loading}>
                                    <FaHistory /> RETORNO
                                </button>
                                <button className="pr-btn-action btn-secondary" onClick={() => enviarAcao("RABO_GATA")} disabled={loading}>
                                    <FaCat /> RABO GATA
                                </button>
                                <button className="pr-btn-action btn-dark" onClick={() => enviarAcao("PULAR")} disabled={loading}>
                                    <FaArrowRight /> PRÓXIMO
                                </button>
                            </>
                        )}

                        {podeJulgar && (
                            <>
                                <button className="pr-btn-action btn-success" onClick={() => enviarAcao("VALIDO")} disabled={loading}>
                                    <FaCheck /> VALEU BOI
                                </button>
                                <button className="pr-btn-action btn-danger" onClick={() => enviarAcao("ZERO")} disabled={loading}>
                                    <FaTimes /> ZERO
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}
          </div>

          {/* LISTA: PRÓXIMOS 3 DA FILA */}
          {rodizio && (
              <div className="pr-next-list">
                  <div className="pr-next-header">PRÓXIMOS NA FILA</div>
                  
                  {proximos.length === 0 ? (
                      <div style={{padding:15, textAlign:"center", opacity:0.6}}>Ninguém na fila</div>
                  ) : (
                    proximos.map((prox, index) => (
                        <div key={prox.numeroSenha} className="pr-next-item">
                            <div style={{display:"flex", alignItems:"center", gap:10}}>
                                <span style={{color:"#666", fontSize:"0.8rem"}}>{index + 1}º</span>
                                <span className="pr-next-badge">#{prox.numeroSenha}</span>
                            </div>
                            <span style={{color:"#fff", fontWeight:"500", fontSize:"0.9rem"}}>
                                {prox.nomeVaqueiro?.split(" ")[0]} 
                                <small style={{color:"#888", marginLeft:5}}>({prox.boiAtual}º boi)</small>
                            </span>
                        </div>
                    ))
                  )}

                  {/* 🔹 FOOTER COM CONTAGEM TOTAL */}
                  <div className="pr-queue-footer">
                      RESTAM CORRER NO RODIZIO: {restamCorrer}
                  </div>
              </div>
          )}
        </div>

        {/* COLUNA DIREITA: LISTAS E ENCAIXE */}
        <div className="pr-sidebar">
            
            {/* Box Encaixe */}
            <div className="pr-list-card">
                <h4 className="pr-list-title"><FaListOl /> Ações Extras</h4>
                <div className="pr-encaixe-box">
                    <input 
                        type="number" 
                        className="pr-input-encaixe" 
                        placeholder="Senha..." 
                        value={encaixeNumero}
                        onChange={e => setEncaixeNumero(e.target.value)}
                    />
                    <button className="pr-btn-encaixe" onClick={encaixar} disabled={loading}>ENCAIXAR</button>
                </div>
                <button className="pr-btn-sair" style={{width:"100%"}} onClick={() => navigate("/painel/disputa")}>
                    Ir para Disputa 🏆
                </button>
            </div>

            {/* Classificadas */}
            <div className="pr-list-card">
                <h4 className="pr-list-title"><FaTrophy /> Classificadas</h4>
                <div style={{maxHeight: "200px", overflowY: "auto"}}>
                    {(rodizio?.classificadas || []).length === 0 ? <span style={{opacity:0.5, fontSize:"0.9rem"}}>Nenhuma classificada.</span> : (
                        rodizio.classificadas.map(c => (
                            <div key={c.numeroSenha} className="pr-list-item win">
                                <div>
                                    <strong style={{color:"#fdd212"}}>#{c.numeroSenha}</strong>
                                    <div style={{fontSize:"0.8rem", opacity:0.8}}>{c.nomeVaqueiro?.split(" ")[0]}</div>
                                </div>
                                {podeJulgar && (
                                    <div style={{display:"flex", gap:5}}>
                                        <button className="pr-btn-encaixe" style={{padding:"2px 8px", fontSize:"0.7rem"}} onClick={() => classificarRodada(c.numeroSenha, 1)}>R1</button>
                                        <button className="pr-btn-encaixe" style={{padding:"2px 8px", fontSize:"0.7rem"}} onClick={() => classificarRodada(c.numeroSenha, 2)}>R2</button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Perdidas (COM MOTIVO DO BOI) */}
            <div className="pr-list-card">
                <h4 className="pr-list-title" style={{color:"#f44336"}}><FaBan /> Perdidas</h4>
                <div style={{maxHeight: "200px", overflowY: "auto"}}>
                    {(rodizio?.perdidas || []).length === 0 ? <span style={{opacity:0.5, fontSize:"0.9rem"}}>Nenhuma perda.</span> : (
                        rodizio.perdidas.map(p => (
                            <div key={p.numeroSenha} className="pr-list-item lost">
                                <div>
                                    <strong style={{color:"#f44336"}}>#{p.numeroSenha}</strong>
                                    <span style={{marginLeft: 8, color:"#fff"}}>{p.nomeVaqueiro?.split(" ")[0]}</span>
                                </div>
                                <div className="pr-info-boi">
                                    No {p.boiAtual}º Boi
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

        </div>
      </div>
    </div>
  );
}